import { useVisualViewport } from "./useVisualViewport";
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { AppIcon, MenuGroup, MenuRow } from './ParticipantShell';
import type { CardProjection } from '../domain/friendship-card-v1';

export function FriendshipMonogram({ name, group = false }: { name?: string; group?: boolean }) {
  return <span className="friendship-monogram" aria-hidden="true">{group ? <AppIcon name="group" /> : Array.from(name?.trim() ?? '')[0] ?? '·'}</span>;
}
export function FriendshipPanel({ label, children }: { label?: string; children: ReactNode }) {
  return <section className="friendship-list-section">{label && <h2 className="friendship-section-label">{label}</h2>}<MenuGroup label={label}>{children}</MenuGroup></section>;
}
export function FriendshipRow({ name, detail, monogram, group, unread = false, onClick, className = '' }: {
  name: string; detail?: string; monogram?: string; className?: string; group?: boolean; unread?: boolean; onClick: () => void;
}) {
  return <MenuRow className={`friendship-list-row ${className}`} onClick={onClick} detail={detail}
    leading={<FriendshipMonogram name={monogram ?? name} group={group} />}
    trailing={<span className="friendship-row-tail" aria-hidden="true">{unread ? <span className="friendship-unread-dot" /> : <AppIcon name="chevron" />}</span>}>
    {name}{unread && <span className="visually-hidden">，有新消息</span>}
  </MenuRow>;
}

/** Native modal focus/inert behavior, shared by the small action and two-step picker sheets. */
export function FriendshipSheet({ open, onClose, title, children, footer, headerAction, className = '', focusSearch = false, showClose = true, error = '' }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode;
  headerAction?: ReactNode; className?: string; focusSearch?: boolean; showClose?: boolean; error?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null), titleId = useId();
  const viewport = useVisualViewport(open);
  const keyboardStyle = viewport ? { maxHeight: viewport.height, marginBottom: `${Math.max(0, window.innerHeight - parseFloat(String(viewport.height)) - parseFloat(String(viewport.top)))}px` } : undefined;
  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open) {
      if (!node.open) node.showModal();
      node.querySelector<HTMLElement>(focusSearch ? 'input' : 'h2')?.focus({ preventScroll: true });
    } else if (node.open) node.close();
  }, [open, focusSearch]);
  return <dialog ref={dialog} className={`account-dialog friendship-sheet ${className}`} aria-labelledby={titleId} style={keyboardStyle}
    onCancel={event => { event.stopPropagation(); onClose(); }}
    onClose={event => { event.stopPropagation(); if (open && event.target === event.currentTarget) onClose(); }}>
    <header className="friendship-sheet-heading">{headerAction}<h2 id={titleId} tabIndex={-1}>{title}</h2>{showClose && <button type="button" className="text-button" onClick={onClose}>Cancel</button>}</header>
    <div className="friendship-sheet-content">{open && error && <p className="error-message" role="alert">{error}</p>}{children}</div>{footer && <div className="friendship-sheet-footer">{footer}</div>}
  </dialog>;
}
export const cardThing = (projection: CardProjection | null | undefined) => projection?.items.find(item => item.id === 'Q01')?.values.join(' ') || '一起做这件事';
const SHORT_LABELS: Record<string, string> = {
  Q02a: '频率', Q02b: '时段', Q03: '地点', Q04: '强度', Q05a: '费用', Q05b: '开销', Q05c: '人均',
  Q06: '物品', Q07: '帮忙', Q08: '变动', Q09: '相处', Q10: '边界', Q11a: '经验', Q11b: '经历',
};
export function FriendshipRequestCard({ projection }: { projection: CardProjection }) {
  return <article className="friendship-request-card">
    <p className="friendship-card-thing">{cardThing(projection)}</p>
    <dl>{projection.items.filter(item => item.id !== 'Q01').map(item => <div className="friendship-card-fact" key={item.id}>
      <dt>{SHORT_LABELS[item.id] ?? item.label}</dt><dd>{item.values.join(' · ')}</dd>
    </div>)}</dl>
  </article>;
}
